<template>
  <div class="auto-push">
    <!-- 统计卡片 -->
    <el-row :gutter="20" class="stat-row">
      <el-col :span="6">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-value">{{ stats.taskCount || 0 }}</div>
          <div class="stat-label">{{ $t('autoPush.statTask') }}</div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-value" style="color: #67C23A;">{{ stats.activeTaskCount || 0 }}</div>
          <div class="stat-label">{{ $t('autoPush.statActive') }}</div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-value" style="color: #409EFF;">{{ stats.conditionCount || 0 }}</div>
          <div class="stat-label">{{ $t('autoPush.statCondition') }}</div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover" class="stat-card">
          <div class="stat-value" style="color: #E6A23C;">{{ stats.todayPushCount || 0 }}</div>
          <div class="stat-label">{{ $t('autoPush.statToday') }}</div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 任务列表区（按钮含新增任务、条件管理等入口） -->
    <el-card>
      <div slot="header" class="card-header">
        <span>{{ $t('autoPush.taskListTitle') }}</span>
        <div>
          <el-button type="success" size="small" @click="showManualPush">{{ $t('autoPush.manualPush') }}</el-button>
          <el-button type="primary" size="small" @click="handleAddTask">{{ $t('autoPush.addTask') }}</el-button>
          <el-button size="small" @click="$router.push('/auto-push/conditions')">{{ $t('autoPush.conditions') }}</el-button>
          <el-button size="small" @click="$router.push('/auto-push/logs')">{{ $t('autoPush.logs') }}</el-button>
          <el-button type="success" size="small" :disabled="selectedTasks.length === 0" @click="handleBatchExecute">{{ $t('autoPush.execSelected') }}</el-button>
        </div>
      </div>

      <!-- 筛选表单 -->
      <el-form inline class="search-form" @submit.native.prevent>
        <el-form-item :label="$t('autoPush.filterName')">
          <el-input v-model="search.name" :placeholder="$t('autoPush.filterNamePh')" clearable style="width: 220px;"></el-input>
        </el-form-item>
        <el-form-item :label="$t('autoPush.filterMode')">
          <el-select v-model="search.mode" :placeholder="$t('autoPush.filterModePh')" clearable style="width: 140px;">
            <el-option :label="$t('autoPush.modePeriodic')" value="periodic"></el-option>
            <el-option :label="$t('autoPush.modeEnrolledWeek')" value="enrolledWeek"></el-option>
          </el-select>
        </el-form-item>
        <!-- 推送动作筛选（2026-10-03）：运营常要单独看「提醒通知」或「推课程」的任务 -->
        <el-form-item :label="$t('autoPush.filterAction')">
          <el-select v-model="search.action" :placeholder="$t('autoPush.filterActionPh')" clearable style="width: 150px;">
            <el-option :label="$t('autoPush.actionContent')" value="content"></el-option>
            <el-option :label="$t('autoPush.actionReminder')" value="reminder"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item>
          <el-checkbox v-model="search.today">{{ $t('autoPush.filterToday') }}</el-checkbox>
        </el-form-item>
        <el-form-item>
          <el-button @click="resetSearch">{{ $t('autoPush.reset') }}</el-button>
        </el-form-item>
      </el-form>

      <!-- 任务列表 -->
      <el-table :data="taskList" border stripe v-loading="loading" @selection-change="handleSelectionChange">
        <el-table-column type="selection" width="50"></el-table-column>
        <el-table-column prop="id" :label="$t('autoPush.colId')" width="70"></el-table-column>
        <!-- 提醒类型（仅提醒任务有意义；标签由后端按注册表给中英两份）
             取当前语言只能用 $getLang()：本项目的 i18n 只挂了 $t / $getLang()（没有 vue-i18n）。
             曾写成 $i18n.locale —— $i18n 不存在，模板渲染时抛 TypeError，
             整张任务表渲染失败、看起来"列表里什么都没有"。 -->
        <el-table-column :label="$t('autoPush.colReminderType')" width="130">
          <template slot-scope="scope">
            <span v-if="scope.row.action === 'reminder'">
              {{ $getLang() === 'en' ? scope.row.reminderTypeLabelEn : scope.row.reminderTypeLabel }}
            </span>
            <span v-else>-</span>
          </template>
        </el-table-column>
        <el-table-column prop="name" :label="$t('autoPush.colName')" min-width="120"></el-table-column>
        <el-table-column :label="$t('autoPush.colPushMode')" width="130">
          <template slot-scope="scope">
            <el-tooltip :content="scope.row.enrolledWeek ? $t('autoPush.modeEnrolledWeek') : $t('autoPush.modePeriodic')" placement="top">
              <el-tag size="mini" :type="scope.row.enrolledWeek ? 'warning' : 'primary'" effect="plain">
                {{ scope.row.enrolledWeek ? $t('autoPush.modeEnrolledWeek') : $t('autoPush.modePeriodic') }}
              </el-tag>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column :label="$t('autoPush.colAction')" width="130">
          <template slot-scope="scope">
            <el-tooltip :content="getActionName(scope.row.action)" placement="top">
              <el-tag size="mini" :type="scope.row.action === 'reminder' ? 'warning' : 'primary'" effect="plain">
                {{ getActionName(scope.row.action) }}
              </el-tag>
            </el-tooltip>
          </template>
        </el-table-column>
        <el-table-column :label="$t('autoPush.colTarget')" min-width="240">
          <template slot-scope="scope">
            <template v-for="t in getTargetTags(scope.row)">
              <el-tag :key="t.key" :type="t.type" size="mini" effect="plain" class="target-tag">{{ t.label }}</el-tag>
            </template>
          </template>
        </el-table-column>
        <el-table-column :label="$t('autoPush.colSendTime')" width="140">
          <template slot-scope="scope">
            {{ formatSendTime(scope.row) }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('autoPush.colStatus')" width="80">
          <template slot-scope="scope">
            <el-tag :type="isActive(scope.row.status) ? 'success' : 'info'" size="mini">
              {{ isActive(scope.row.status) ? $t('autoPush.statusActive') : $t('autoPush.statusInactive') }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column :label="$t('autoPush.colNext')" width="185">
          <template slot-scope="scope">
            <!-- 入组周数任务每天扫描一次、命中条件才发送，此处时间是扫描时间而非发送时间 -->
            <el-tooltip v-if="scope.row.enrolledWeek" :content="$t('autoPush.colNextScanTip')" placement="top">
              <span class="next-scan-tag">{{ $t('autoPush.colNextScan') }}</span>
            </el-tooltip>
            {{ scope.row.nextExecutedAt || '-' }}
          </template>
        </el-table-column>
        <el-table-column :label="$t('autoPush.colAction')" width="280" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" @click="handleEditTask(scope.row)">{{ $t('autoPush.edit') }}</el-button>
            <el-button type="text" @click="handlePreview(scope.row)">{{ $t('autoPush.preview') }}</el-button>
            <el-button type="text" @click="handleExecute(scope.row)">{{ $t('autoPush.execute') }}</el-button>
            <el-button type="text" @click="handleToggle(scope.row)">{{ isActive(scope.row.status) ? $t('autoPush.toggleDisable') : $t('autoPush.toggleEnable') }}</el-button>
            <el-button type="text" style="color: #F56C6C;" @click="handleDelete(scope.row)">{{ $t('autoPush.delete') }}</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <el-pagination
        class="pagination"
        @current-change="handlePageChange"
        :current-page="taskPage"
        :page-size="taskPageSize"
        :total="taskTotal"
        layout="total, prev, pager, next"
      ></el-pagination>
    </el-card>

    <!-- 手动推送对话框 -->
    <el-dialog :title="$t('autoPush.manualTitle')" :visible.sync="manualPushVisible" width="600px" :close-on-click-modal="false">
      <el-form :model="manualForm" label-width="100px" :rules="manualRules" ref="manualForm">
        <el-form-item :label="$t('autoPush.mContent')" prop="contentId">
          <el-select v-model="manualForm.contentId" :placeholder="$t('autoPush.mContent')" filterable clearable style="width: 100%" :popper-class="'wc-content-popper'">
            <el-option v-for="c in contentOptions" :key="c.id" :label="c.title" :value="c.id">
              <span class="opt-title">{{ c.title }}</span>
              <el-tag v-if="c.isBilingual" size="mini" type="info" class="opt-lang">双语</el-tag>
              <el-tag v-else size="mini" :type="c.languages[0] === 'en' ? 'warning' : 'success'" class="opt-lang">
                {{ c.languages[0] === 'en' ? 'English' : '中文' }}
              </el-tag>
            </el-option>
          </el-select>
        </el-form-item>
        <el-divider content-position="left">{{ $t('autoPush.mUserScope') }}</el-divider>
        <el-form-item :label="$t('autoPush.mUserScope')" prop="targetType">
          <el-radio-group v-model="manualForm.targetType" @change="handleManualTargetChange">
            <el-radio label="all">{{ $t('autoPush.mAll') }}</el-radio>
            <el-radio label="users">{{ $t('autoPush.mUser') }}</el-radio>
            <el-radio label="none">{{ $t('autoPush.mNone') }}</el-radio>
          </el-radio-group>
          <div v-if="manualForm.targetType === 'all'" class="form-tip">
            <i class="el-icon-info"></i> {{ $t('autoPush.mAllTip') }}
          </div>
          <div v-if="manualForm.targetType === 'users'" class="form-tip">
            <i class="el-icon-info"></i> {{ $t('autoPush.mUsersTip') }}
          </div>
        </el-form-item>
        <el-form-item v-if="manualForm.targetType === 'users'" :label="$t('autoPush.mTargetUser')" prop="targetUserIds">
          <el-select v-model="manualForm.targetUserIds" :placeholder="$t('autoPush.phTargetUser')" filterable multiple clearable style="width: 100%">
            <el-option v-for="u in userOptions" :key="u.id" :label="u.nickname || '用户' + u.id" :value="u.id"></el-option>
          </el-select>
        </el-form-item>

        <el-divider content-position="left">{{ $t('autoPush.mFilter') }}</el-divider>
        <el-form-item :label="$t('autoPush.mFilter')">
          <el-checkbox-group v-model="manualForm.filterTypes" :disabled="manualForm.targetType === 'all'" @change="handleFilterChange">
            <el-checkbox label="batch">{{ $t('autoPush.mBatch') }}</el-checkbox>
            <el-checkbox label="group">{{ $t('autoPush.mGroup') }}</el-checkbox>
            <el-checkbox label="condition">{{ $t('autoPush.mCondition') }}</el-checkbox>
          </el-checkbox-group>
          <div class="form-tip">
            <i class="el-icon-info"></i> {{ $t('autoPush.mFilterTip') }}
          </div>
        </el-form-item>
        <el-form-item v-if="manualForm.filterTypes.includes('batch')" :label="$t('autoPush.mTargetBatch')" prop="targetBatchId">
          <el-select v-model="manualForm.targetBatchId" :placeholder="$t('autoPush.phTargetBatch')" filterable clearable style="width: 100%">
            <el-option v-for="b in batchOptions" :key="b.id" :label="b.name" :value="b.id"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item v-if="manualForm.filterTypes.includes('group')" :label="$t('autoPush.mTargetGroup')" prop="targetGroupId">
          <el-select v-model="manualForm.targetGroupId" :placeholder="$t('autoPush.phTargetGroup')" filterable clearable style="width: 100%">
            <el-option v-for="g in groupOptions" :key="g.id" :label="g.name" :value="g.id"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item v-if="manualForm.filterTypes.includes('condition')" :label="$t('autoPush.mTargetCondition')" prop="conditionId">
          <el-select v-model="manualForm.conditionId" :placeholder="$t('autoPush.phCondition')" filterable clearable style="width: 100%">
            <el-option v-for="c in conditionOptions" :key="c.id" :label="c.name" :value="c.id"></el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button @click="manualPushVisible = false">{{ $t('autoPush.mCancel') }}</el-button>
        <el-button type="primary" :loading="manualPushing" @click="handleManualPush">{{ $t('autoPush.mSubmit') }}</el-button>
      </div>
    </el-dialog>

    <!-- 任务干跑预览 -->
    <el-dialog :title="$t('autoPush.previewTitle')" :visible.sync="previewVisible" width="640px">
      <div v-if="previewLoading" class="preview-loading">
        <i class="el-icon-loading"></i> {{ $t('autoPush.previewLoading') }}
      </div>
      <div v-else-if="previewData" class="preview-body">
        <div class="preview-header">
          <span class="preview-name">{{ previewData.taskName }}</span>
          <el-tag size="mini" :type="previewData.action === 'reminder' ? 'warning' : 'primary'" effect="plain">
            {{ getActionName(previewData.action) }}
          </el-tag>
        </div>
        <!-- 排程摘要：一眼核对「第几周 / 周几 / 几点」 -->
        <div v-if="scheduleSummary()" class="preview-schedule">{{ scheduleSummary() }}</div>

        <!-- 排程命中提示 -->
        <div v-if="previewData.isWeekBased && !previewData.sendDayMatch" class="preview-warn">
          <i class="el-icon-warning"></i> {{ $t('autoPush.previewSendDayMiss') }}
        </div>

        <!-- 目标用户 -->
        <div class="preview-section">
          <div class="preview-section-title">{{ $t('autoPush.previewTarget') }}（{{ previewData.targetUserCount }} {{ $t('autoPush.previewUsers') }}）</div>
          <div v-if="previewData.sampleUsers && previewData.sampleUsers.length" class="preview-users">
            <el-tag v-for="u in previewData.sampleUsers" :key="u.id" size="mini" class="preview-user-tag">
              {{ u.nickname || ('#' + u.id) }}{{ u.studyId ? '（' + u.studyId + '）' : '' }}
            </el-tag>
            <span v-if="previewData.targetUserCount > previewData.sampleUsers.length" class="preview-more">
              ...{{ $t('autoPush.previewMore', { n: previewData.targetUserCount - previewData.sampleUsers.length }) }}
            </span>
          </div>
          <div v-else class="preview-empty">{{ $t('autoPush.previewNoUsers') }}</div>
        </div>

        <!-- 通知触达预估：开关关闭 / 缺邮箱缺令牌的用户收不到提醒 -->
        <div v-if="previewData.reachability" class="preview-section">
          <div class="preview-section-title">
            {{ $t('autoPush.previewReach') }}
            <el-tooltip :content="$t('autoPush.previewReachTip')" placement="top">
              <i class="el-icon-info preview-sample-icon"></i>
            </el-tooltip>
          </div>
          <div class="preview-reach">
            <span>{{ $t('autoPush.previewReachEmail') }} <b>{{ previewData.reachability.email }}</b> {{ $t('autoPush.previewUsers') }}</span>
            <span>{{ $t('autoPush.previewReachIos') }} <b>{{ previewData.reachability.ios }}</b> {{ $t('autoPush.previewUsers') }}</span>
            <span :class="{ 'reach-none': previewData.reachability.none > 0 }">
              {{ $t('autoPush.previewReachNone') }} <b>{{ previewData.reachability.none }}</b> {{ $t('autoPush.previewUsers') }}
            </span>
          </div>
          <div v-if="previewData.reachability.none > 0" class="preview-sample-note">
            {{ previewData.action === 'reminder'
              ? $t('autoPush.previewReachNoneReminderNote')
              : $t('autoPush.previewReachNoneContentNote') }}
          </div>
        </div>

        <!-- 预计下次执行（入组周数任务实为每日扫描，命中条件才发送） -->
        <div v-if="previewData.nextRunAt" class="preview-section">
          <div class="preview-section-title">
            {{ previewData.isWeekBased ? $t('autoPush.previewNextScan') : $t('autoPush.previewNextRun') }}
          </div>
          <div class="preview-next-run">{{ previewData.nextRunAt }}</div>
          <div v-if="previewData.isWeekBased" class="preview-sample-note">
            {{ $t('autoPush.previewNextScanNote') }}
          </div>
        </div>

        <!-- 提醒内容 -->
        <div v-if="previewData.willSendReminder" class="preview-section">
          <div class="preview-section-title">
            {{ $t('autoPush.previewReminder') }}
            <el-tooltip v-if="previewData.willSendReminder.isSample" :content="$t('autoPush.previewSampleTip')" placement="top">
              <i class="el-icon-info preview-sample-icon"></i>
            </el-tooltip>
          </div>
          <div class="preview-content">
            <div><b>{{ $t('autoPush.previewTitle2') }}：</b>{{ previewData.willSendReminder.title }}</div>
            <div><b>{{ $t('autoPush.previewBody') }}：</b>{{ previewData.willSendReminder.body }}</div>
            <div v-if="previewData.willSendReminder.unreadTitles && previewData.willSendReminder.unreadTitles.length" class="preview-unread">
              <div>{{ $t('autoPush.previewUnreadList') }}：</div>
              <div v-for="(t, i) in previewData.willSendReminder.unreadTitles" :key="i" class="preview-unread-item">{{ t }}</div>
            </div>
            <div v-if="previewData.willSendReminder.isSample" class="preview-sample-note">
              {{ $t('autoPush.previewSampleNote') }}
            </div>
            <!-- 提醒文案按「用户语言」生成：首位样本用户是英文时，预览显示的即英文文案。
                 不标明语言会让运营误以为配置错了 -->
            <div v-if="previewData.willSendReminder.sampleLanguage" class="preview-sample-note">
              {{ $t('autoPush.previewReminderLang') }}：{{ previewData.willSendReminder.sampleLanguage === 'en' ? 'English' : '中文' }}
            </div>
            <!-- 提醒文案按「提醒类型」写死（中英各一份），不再有"运营漏填英文"的提示项 -->
          </div>
        </div>

        <!-- 课程内容 -->
        <div v-else-if="previewData.willPushContent" class="preview-section">
          <div class="preview-section-title">
            {{ $t('autoPush.previewContent') }}
            <el-tooltip :content="$t('autoPush.previewLanguageTip')" placement="top">
              <i class="el-icon-info preview-sample-icon"></i>
            </el-tooltip>
          </div>
          <div class="preview-content">
            <div v-for="(c, i) in previewData.willPushContent" :key="i" class="preview-content-item">
              {{ c.title }}<span v-if="c.contentId" class="preview-content-id">{{ $t('autoPush.previewContentId', { id: c.contentId }) }}</span>
              <!-- 目标语言版本缺失：后端只回传语言码，文案在此按界面语言渲染 -->
              <div v-if="c.missingLanguage" class="preview-lang-warn">
                <i class="el-icon-warning"></i> {{ $t('autoPush.previewMissingLang', { lang: langLabel(c.missingLanguage) }) }}
              </div>
              <!-- 语言兜底：老课程缺目标语言版本时会推原语言版，人数与语言码由后端给出 -->
              <div v-for="(w, wi) in (c.languageFallback || [])" :key="wi" class="preview-lang-warn">
                <i class="el-icon-warning"></i>
                {{ $t('autoPush.previewLangFallback', { count: w.userCount, userLang: langLabel(w.userLanguage), contentLang: langLabel(w.contentLanguage) }) }}
              </div>
            </div>
            <div v-if="previewData.expectedPushCount !== undefined" class="preview-push-count">
              {{ $t('autoPush.previewExpectedPush') }}<b>{{ previewData.expectedPushCount }}</b>
              <span v-if="previewData.isWeekBased" class="preview-sample-note">（{{ $t('autoPush.previewAfterDedup') }}）</span>
            </div>
          </div>
        </div>
      </div>
      <span slot="footer">
        <el-button @click="previewVisible = false">{{ $t('autoPush.close') }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getIndex, deleteTask, toggleTask, executeNow, getConditions, previewTask } from '@/api/autoPush'
import { getContents, getUsers, save } from '@/api/pushes'
import { getList as getGroupList } from '@/api/userGroup'
import { getList as getBatchList } from '@/api/userBatch'

export default {
  name: 'AutoPush',
  data() {
    return {
      stats: {},
      taskList: [],
      loading: false,
      taskPage: 1,
      taskPageSize: 15,
      taskTotal: 0,
      selectedTasks: [],
      // 筛选条件
      search: {
        name: '',
        mode: '',
        // 推送动作：'' = 全部 / content = 推课程内容 / reminder = 发提醒通知
        action: '',
        today: false
      },
      // 干跑预览
      previewVisible: false,
      previewLoading: false,
      previewData: null,
      // 手动推送
      manualPushVisible: false,
      manualPushing: false,
      manualForm: {
        pushType: 'content',
        contentId: '',
        targetType: 'all',
        targetUserIds: [],
        filterTypes: [],
        targetBatchId: '',
        targetGroupId: '',
        conditionId: ''
      },
      manualRules: {
        pushType: [{ required: true, message: this.$t('autoPush.rulePushType'), trigger: 'change' }],
        contentId: [{ required: true, message: this.$t('autoPush.ruleContent'), trigger: 'change' }],
        targetType: [{ required: true, message: this.$t('autoPush.ruleTarget'), trigger: 'change' }],
        targetGroupId: [{ required: true, message: this.$t('autoPush.ruleTargetGroup'), trigger: 'change' }],
        targetBatchId: [{ required: true, message: this.$t('autoPush.ruleTargetBatch'), trigger: 'change' }],
        targetUserIds: [{ required: true, message: this.$t('autoPush.ruleTargetUser'), trigger: 'change' }],
        conditionId: [{ required: true, message: this.$t('autoPush.ruleTargetCondition'), trigger: 'change' }]
      },
      contentOptions: [],
      groupOptions: [],
      batchOptions: [],
      conditionOptions: [],
      userOptions: []
    }
  },
  created() {
    this.loadData()
  },
  watch: {
    // 筛选条件变化即时刷新（防抖 300ms，避免输入过程频繁请求）
    search: {
      deep: true,
      handler() {
        clearTimeout(this._searchTimer)
        this._searchTimer = setTimeout(() => {
          this.taskPage = 1
          this.selectedTasks = []
          this.loadData()
        }, 300)
      }
    }
  },
  beforeDestroy() {
    clearTimeout(this._searchTimer)
  },
  methods: {
    // 加载首页数据
    loadData() {
      this.loading = true
      const params = {
        page: this.taskPage,
        pageSize: this.taskPageSize,
        name: this.search.name || undefined,
        mode: this.search.mode || undefined,
        action: this.search.action || undefined,
        today: this.search.today ? true : undefined
      }
      getIndex(params).then(res => {
        this.stats = res.data.stats || {}
        const tasksData = res.data.tasks || {}
        this.taskList = tasksData.list || tasksData || []
        this.taskTotal = tasksData.total || (Array.isArray(this.taskList) ? this.taskList.length : 0)
      }).catch(() => {
        this.$message.error(this.$t('autoPush.loadFail'))
      }).finally(() => {
        this.loading = false
      })
    },
    // 任务列表翻页：清空选中，避免跨页选中态失效
    handlePageChange(page) {
      this.taskPage = page
      this.selectedTasks = []
      this.loadData()
    },
    // 重置筛选：清空条件后 watch 会自动触发加载，这里直接回到第 1 页
    resetSearch() {
      this.search = { name: '', mode: '', action: '', today: false }
    },
    // 新增任务
    handleAddTask() {
      this.$router.push('/auto-push/task-edit')
    },
    // 编辑任务
    handleEditTask(row) {
      this.$router.push('/auto-push/task-edit/' + row.id)
    },
    // 任务干跑预览
    handlePreview(row) {
      this.previewVisible = true
      this.previewLoading = true
      this.previewData = null
      previewTask(row.id).then(res => {
        this.previewData = res.data || {}
      }).catch(() => {
        this.$message.error(this.$t('autoPush.previewFail'))
        this.previewVisible = false
      }).finally(() => {
        this.previewLoading = false
      })
    },
    // 执行任务
    handleExecute(row) {
      // 禁用任务点击"执行"时额外提示，确认后仍可强制推送
      const confirmText = this.isActive(row.status)
        ? this.$t('autoPush.executeConfirm')
        : this.$t('autoPush.executeDisabledConfirm')
      this.$confirm(confirmText, this.$t('common.tip'), {
        confirmButtonText: this.$t('common.confirm'),
        cancelButtonText: this.$t('common.cancel'),
        type: 'warning'
      }).then(() => {
        executeNow({ task_ids: [row.id] }).then(() => {
          this.$message.success(this.$t('autoPush.executeSuccess'))
          this.loadData()
        }).catch(() => {
          this.$message.error(this.$t('autoPush.executeFail'))
        })
      }).catch(() => {})
    },
    // 批量执行
    handleBatchExecute() {
      this.$confirm(this.$t('autoPush.batchExecConfirm', { n: this.selectedTasks.length }), this.$t('common.tip'), {
        confirmButtonText: this.$t('common.confirm'),
        cancelButtonText: this.$t('common.cancel'),
        type: 'warning'
      }).then(() => {
        // 一次调用传全部任务ID，由后端逐个执行；避免循环调用触发"执行所有到期任务"
        const taskIds = this.selectedTasks.map(t => t.id)
        executeNow({ task_ids: taskIds }).then(() => {
          this.$message.success(this.$t('autoPush.batchExecSuccess'))
          this.loadData()
        }).catch(() => {
          this.$message.error(this.$t('autoPush.batchExecFail'))
        })
      }).catch(() => {})
    },
    // 切换启用/禁用
    handleToggle(row) {
      toggleTask(row.id).then(() => {
        this.$message.success(this.$t('autoPush.toggleSuccess'))
        this.loadData()
      }).catch(() => {
        this.$message.error(this.$t('autoPush.toggleFail'))
      })
    },
    // 删除任务
    async handleDelete(row) {
      try {
        await this.$confirm(this.$t('autoPush.deleteConfirm'), this.$t('common.tip'), {
          confirmButtonText: this.$t('common.confirm'),
          cancelButtonText: this.$t('common.cancel'),
          type: 'warning'
        })
        deleteTask(row.id).then(() => {
          this.$message.success(this.$t('autoPush.deleteSuccess'))
          this.loadData()
        }).catch(() => {
          this.$message.error(this.$t('autoPush.deleteFail'))
        })
      } catch (e) {
        // 取消
      }
    },
    // 选择变更
    handleSelectionChange(val) {
      this.selectedTasks = val
    },
    // 获取类型名称
    getTypeName(type) {
      const map = { daily: this.$t('autoPush.typeDaily'), weekly: this.$t('autoPush.typeWeekly'), monthly: this.$t('autoPush.typeMonthly') }
      return map[type] || type
    },
    // 获取动作名称
    getActionName(action) {
      const map = { content: this.$t('autoPush.actionContent'), reminder: this.$t('autoPush.actionReminder') }
      return map[action] || action
    },
    // 语言码 → 本地化名称（复用 common 下的中英命名，与推送日志页一致）
    langLabel(lang) {
      return lang === 'en' ? this.$t('common.langEn') : this.$t('common.langZh')
    },
    // 获取目标名称
    getTargetName(type) {
      const map = { all: this.$t('autoPush.targetAll'), batch: this.$t('autoPush.targetBatch'), group: this.$t('autoPush.targetGroup'), condition: this.$t('autoPush.targetCondition'), combined: this.$t('autoPush.targetCombined') }
      return map[type] || type
    },
    // 把任务的目标渲染成 tag 列表：全部用户绿色；批次蓝、分组橙、条件紫
    getTargetTags(row) {
      const tags = []
      if (row.targetType === 'all') {
        tags.push({ key: 'all', type: 'success', label: this.$t('autoPush.targetAll') })
      } else {
        if (row.targetBatchName) tags.push({ key: 'b', type: 'primary', label: `${this.$t('autoPush.targetBatch')}:${row.targetBatchName}` })
        // 字段名是 targetGroupNames（复数）：多分组改造后后端返回「Low burden、High burden」这种
        // 「、」连接的字符串，这里曾误读单数 targetGroupName，导致分组永远不显示
        if (row.targetGroupNames) tags.push({ key: 'g', type: 'warning', label: `${this.$t('autoPush.targetGroup')}:${row.targetGroupNames}` })
        if (row.conditionName) tags.push({ key: 'c', type: 'info', label: `${this.$t('autoPush.targetCondition')}:${row.conditionName}` })
        // 兜底：后端没返回 name 时显示抽象标签
        if (!tags.length) {
          tags.push({ key: 't', type: 'info', label: this.getTargetName(row.targetType) })
        }
      }
      // 入组周数投放任务：在目标处标识目标入组周数
      if (row.enrolledWeek) {
        tags.push({ key: 'w', type: 'warning', label: `${this.$t('autoPush.labelEnrolledWeek')}:${row.enrolledWeek}` })
      }
      return tags
    },
    // 判断是否启用（兼容 1/0 和 active/inactive）
    isActive(status) {
      return status === 'active' || status === 1 || status === '1'
    },
    // 格式化发送时间
    formatSendTime(row) {
      const time = row.sendTime || '-'
      const days = ['', this.$t('autoPush.weekday1'), this.$t('autoPush.weekday2'), this.$t('autoPush.weekday3'), this.$t('autoPush.weekday4'), this.$t('autoPush.weekday5'), this.$t('autoPush.weekday6'), this.$t('autoPush.weekday7')]
      // 入组周数投放：type 存库时被固定写成 daily，周几只存在于 sendDay，
      // 若不单独拼出来，列表里完全看不出这个任务定在周几，只能点进编辑页才知道
      if (row.enrolledWeek && row.sendDay) {
        return days[row.sendDay] + ' ' + time
      }
      if (row.type === 'weekly' && row.sendDay) {
        return days[row.sendDay] + ' ' + time
      }
      if (row.type === 'monthly' && row.sendDay) {
        return this.$t('autoPush.monthDayTipMonth', { day: row.sendDay }) + ' ' + time
      }
      return time
    },
    // 预览弹窗的排程摘要：入组周数投放显示「入组第 N 周 · 周六 21:56（美东）」，
    // 周期推送显示「每周三 19:00 / 每月15号 09:00 / 每天 09:00」，供运营核对配置
    scheduleSummary() {
      const d = this.previewData
      if (!d) return ''
      const time = d.sendTime || '-'
      const tz = this.$t('autoPush.summaryTimezone')
      const days = ['', this.$t('autoPush.weekday1'), this.$t('autoPush.weekday2'), this.$t('autoPush.weekday3'), this.$t('autoPush.weekday4'), this.$t('autoPush.weekday5'), this.$t('autoPush.weekday6'), this.$t('autoPush.weekday7')]
      if (d.isWeekBased) {
        const weekText = this.$t('autoPush.summaryWeek', { n: d.enrolledWeek })
        return d.sendDay
          ? `${weekText} · ${days[d.sendDay]} ${time}（${tz}）`
          : `${weekText} · ${time}（${tz}）`
      }
      if (d.sendType === 'weekly' && d.sendDay) {
        return `${days[d.sendDay]} ${time}（${tz}）`
      }
      if (d.sendType === 'monthly' && d.sendDay) {
        return `${this.$t('autoPush.monthDayTipMonth', { day: d.sendDay })} ${time}（${tz}）`
      }
      return `${this.$t('autoPush.typeDailyBtn')} ${time}（${tz}）`
    },
    // 显示手动推送对话框
    showManualPush() {
      this.manualForm = {
        pushType: 'content',
        contentId: '',
        title: '',
        content: '',
        targetType: 'all',
        targetUserIds: [],
        filterTypes: [],
        targetBatchId: '',
        targetGroupId: '',
        conditionId: ''
      }
      this.manualPushVisible = true
      this.loadManualPushOptions()
    },
    // 加载手动推送选项
    loadManualPushOptions() {
      getContents().then(res => {
        const list = res.data.contents || res.data || []
        this.contentOptions = this.buildContentGroups(list)
      }).catch(() => {})
      getUsers().then(res => {
        this.userOptions = res.data.users || res.data || []
      }).catch(() => {})
      // 使用独立接口获取分组和批次选项
      getGroupList().then(res => {
        this.groupOptions = res.data || []
      }).catch(() => {})
      getBatchList().then(res => {
        this.batchOptions = res.data || []
      }).catch(() => {})
      // 条件列表（与自动推送任务编辑页一致）
      getConditions().then(res => {
        this.conditionOptions = res.data || []
      }).catch(() => {})
    },
    // 按语言组聚合：同一 languageGroupId 的中英两版合并为一项，存组代表 contentId
    buildContentGroups(list) {
      const map = {}
      list.forEach(c => {
        const gid = c.languageGroupId || c.id
        if (!map[gid]) {
          map[gid] = { id: c.id, languageGroupId: gid, languages: [], titles: [] }
        }
        if (map[gid].languages.indexOf(c.language) === -1) {
          map[gid].languages.push(c.language)
        }
        map[gid].titles.push({ language: c.language, title: c.title })
      })
      return Object.keys(map).map(gid => {
        const g = map[gid]
        const isBilingual = g.languages.length > 1
        // 双语课程把中英标题都展示出来（中文在前，英文在后）
        const title = isBilingual
          ? g.titles.slice().sort((a, b) => {
              if (a.language === 'zh') return -1
              if (b.language === 'zh') return 1
              return 0
            }).map(t => t.title).join(' / ')
          : (g.titles[0] && g.titles[0].title)
        return {
          id: g.id,
          title,
          languageGroupId: g.languageGroupId,
          languages: g.languages,
          isBilingual
        }
      })
    },
    // 用户范围变更：选「全部用户」时清空筛选条件组（互斥语义）
    handleManualTargetChange(val) {
      if (val === 'all') {
        this.manualForm.filterTypes = []
        this.manualForm.targetBatchId = ''
        this.manualForm.targetGroupId = ''
        this.manualForm.conditionId = ''
      }
    },
    // 筛选条件勾选变更：取消勾选时联动清空对应下拉值，避免"未勾选但残留旧值"
    handleFilterChange(val) {
      if (!val.includes('batch')) this.manualForm.targetBatchId = ''
      if (!val.includes('group')) this.manualForm.targetGroupId = ''
      if (!val.includes('condition')) this.manualForm.conditionId = ''
    },
    // 组装后端 targetType：全部用户=all；仅指定用户（无筛选）=users；含筛选维度统一为 combined 取交集
    buildManualTargetType() {
      if (this.manualForm.targetType === 'all') return 'all'
      const hasFilter = this.manualForm.filterTypes.length > 0
      if (!hasFilter && this.manualForm.targetType === 'users') return 'users'
      return 'combined'
    },
    // 执行手动推送
    handleManualPush() {
      this.$refs.manualForm.validate(valid => {
        if (!valid) return
        // 额外校验：筛选维度勾选但未选具体值
        if (this.manualForm.filterTypes.includes('batch') && !this.manualForm.targetBatchId) {
          this.$message.warning(this.$t('autoPush.warnBatch'))
          return
        }
        if (this.manualForm.filterTypes.includes('group') && !this.manualForm.targetGroupId) {
          this.$message.warning(this.$t('autoPush.warnGroup'))
          return
        }
        if (this.manualForm.filterTypes.includes('condition') && !this.manualForm.conditionId) {
          this.$message.warning(this.$t('autoPush.warnCondition'))
          return
        }
        // 不限用户范围且未选任何筛选维度 → 无目标
        if (this.manualForm.targetType === 'none' && this.manualForm.filterTypes.length === 0) {
          this.$message.warning(this.$t('autoPush.warnFilterEmpty'))
          return
        }
        this.manualPushing = true
        const data = {
          pushType: this.manualForm.pushType,
          contentId: this.manualForm.contentId,
          targetType: this.buildManualTargetType()
        }
        if (data.targetType === 'combined') {
          // 组合交集：把勾选的具体维度都带上，由后端取交集
          if (this.manualForm.targetUserIds.length) data.targetUserIds = this.manualForm.targetUserIds
          if (this.manualForm.targetBatchId) data.targetBatchId = this.manualForm.targetBatchId
          if (this.manualForm.targetGroupId) data.targetGroupId = this.manualForm.targetGroupId
          if (this.manualForm.conditionId) data.conditionId = this.manualForm.conditionId
        } else if (data.targetType === 'users') {
          data.targetUserIds = this.manualForm.targetUserIds
        }
        save(data).then(res => {
          // 管理端接口统一返回 { code: 1, data: ..., msg: ... }
          // 进入 then 即表示成功（code === 1）
          this.$message.success(res.msg || this.$t('autoPush.pushSuccess'))
          this.manualPushVisible = false
          this.loadData()
        }).catch(() => {
          this.$message.error(this.$t('autoPush.pushFail'))
        }).finally(() => {
          this.manualPushing = false
        })
      })
    }
  }
}
</script>

<style scoped>
.stat-row {
  margin-bottom: 20px;
}
.opt-title {
  display: inline-block;
  word-break: break-all;
  vertical-align: middle;
}
.opt-lang {
  margin-left: 6px;
  vertical-align: middle;
}
.stat-card {
  text-align: center;
  padding: 10px 0;
}
.pagination {
  margin-top: 20px;
  text-align: right;
}
.search-form {
  margin-bottom: 15px;
}
.search-form .el-form-item {
  margin-bottom: 0;
}
.form-tip {
  margin-top: 4px;
  font-size: 12px;
  color: #909399;
  line-height: 1.6;
}
.target-tag {
  margin-right: 4px;
  margin-bottom: 2px;
  max-width: 220px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.stat-value {
  font-size: 28px;
  font-weight: bold;
  color: #303133;
}
.stat-label {
  font-size: 14px;
  color: #909399;
  margin-top: 5px;
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: bold;
  font-size: 16px;
}
.preview-loading {
  text-align: center;
  padding: 30px 0;
  color: #909399;
}
.preview-body {
  max-height: 480px;
  overflow-y: auto;
}
.preview-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.preview-schedule {
  display: inline-block;
  margin-bottom: 12px;
  font-size: 13px;
  color: #606266;
  background: #f7f9fc;
  border-radius: 4px;
  padding: 6px 10px;
}
.preview-name {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}
.preview-warn {
  background: #fdf6ec;
  border: 1px solid #faecd8;
  color: #e6a23c;
  border-radius: 4px;
  padding: 8px 12px;
  font-size: 13px;
  margin-bottom: 12px;
}
.preview-section {
  margin-bottom: 16px;
}
.preview-section-title {
  font-size: 13px;
  font-weight: 600;
  color: #909399;
  margin-bottom: 8px;
}
.preview-users {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.preview-user-tag {
  margin-right: 0;
}
.preview-more {
  color: #909399;
  font-size: 12px;
  line-height: 24px;
}
.preview-empty {
  color: #909399;
  font-size: 13px;
}
.preview-content {
  background: #f7f9fc;
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 13px;
  color: #303133;
  line-height: 1.8;
}
.preview-unread {
  margin-top: 6px;
  color: #606266;
}
.preview-unread-item {
  padding-left: 12px;
  color: #909399;
}
.preview-sample-icon {
  color: #909399;
  font-size: 14px;
  margin-left: 4px;
  cursor: help;
}
.preview-sample-note {
  color: #909399;
  font-size: 12px;
  margin-top: 6px;
  font-style: italic;
}
.preview-push-count {
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px dashed #ebeef5;
  color: #303133;
}
.preview-push-count b {
  color: #409EFF;
  font-size: 15px;
  margin: 0 2px;
}
.preview-reach {
  display: flex;
  gap: 18px;
  flex-wrap: wrap;
  font-size: 13px;
  color: #606266;
}
.preview-reach b {
  color: #409EFF;
  margin: 0 2px;
}
.reach-none,
.reach-none b {
  color: #E6A23C;
}
.preview-lang-warn {
  color: #E6A23C;
  font-size: 12px;
  margin-top: 3px;
}
.next-scan-tag {
  display: inline-block;
  font-size: 11px;
  line-height: 16px;
  color: #E6A23C;
  border: 1px solid #f5dab1;
  background: #fdf6ec;
  border-radius: 3px;
  padding: 0 4px;
  margin-right: 4px;
  cursor: help;
}
.preview-next-run {
  background: #f7f9fc;
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}
</style>

<style>
/* 全局：推送内容下拉框最小宽度，确保长标题完整展示（不受 scoped 影响） */
.wc-content-popper {
  min-width: 580px !important;
}
</style>
